import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  CircularProgress,
  Chip,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, PhotoLibrary as LibraryIcon, Close as CloseIcon } from '@mui/icons-material';
import { BaseButton } from '../BaseButton';
import { LabelWithInfo } from './LabelWithInfo';
import { isImageUrl, isVideoUrl } from './helpers';
import mediaService from '../../../../services/MediaService';
import CategoryService from '../../../../services/CategoryService';
import BaseModal from '../BaseModal';
import { ControlComponentProps } from '../../../../types/ControlTypes';

interface MediaItem {
  _id?: string;
  id?: string;
  url: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
}

/**
 * Upload control — two modes:
 *  1. chooseFromAsset=true: pick from media library (BaseModal)
 *  2. default: upload from local disk (to configured category)
 */
export const UploadControl: React.FC<ControlComponentProps> = ({
  value,
  onChange,
  label,
  description,
  required,
  control,
}) => {
  const [uploading, setUploading] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [mediaList, setMediaList] = React.useState<MediaItem[]>([]);
  const [loadingMedia, setLoadingMedia] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState<string>('');
  const [mimeType, setMimeType] = React.useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const settings = control?.settings || {};
  const chooseFromAsset: boolean = !!settings.chooseFromAsset;
  const configuredCategory: string = settings.category || '';

  // ── Resolve category ID from category name/slug when needed ────────────────
  React.useEffect(() => {
    if (!configuredCategory) return;
    CategoryService.getAllCategories().then((cats: any[]) => {
      const match = cats.find(
        (c) => c.name === configuredCategory || c.slug === configuredCategory || c._id === configuredCategory || c.id === configuredCategory
      );
      if (match) setCategoryId(match._id || match.id || '');
    }).catch(() => {});
  }, [configuredCategory]);

  // ── Load media for picker ──────────────────────────────────────────────────
  const loadMedia = async () => {
    setLoadingMedia(true);
    try {
      const result = await mediaService.listMedia(1, 48, undefined, categoryId || undefined);
      setMediaList((result as any).docs || []);
    } catch {
      setMediaList([]);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleOpenPicker = () => {
    setPickerOpen(true);
    loadMedia();
  };

  const handlePickMedia = (item: MediaItem) => {
    onChange(item.url);
    setMimeType(item.mimeType || '');
    setPickerOpen(false);
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await mediaService.uploadFile(file, true, [], '', categoryId);
      const fileUrl = (response as any)?.url || (response as any)?.data?.url || '';
      if (fileUrl) {
        onChange(fileUrl);
        setMimeType(file.type);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isImage = value && (mimeType.startsWith('image/') || isImageUrl(value));
  const isVideo = value && (mimeType.startsWith('video/') || isVideoUrl(value));

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 1 }}>
        <LabelWithInfo label={label || ''} description={description} required={required} />
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileSelect}
        accept={settings.accept || settings.allow || 'image/*'}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {chooseFromAsset ? (
          <BaseButton
            onClick={handleOpenPicker}
            startIcon={<LibraryIcon />}
            variant="outlined"
            colorVariant="secondary"
            size="small"
          >
            {value ? 'Change Asset' : 'Choose from Assets'}
          </BaseButton>
        ) : (
          <BaseButton
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            isLoading={uploading}
            loadingLabel="Uploading..."
            startIcon={<CloudUploadIcon />}
            variant="outlined"
            colorVariant="secondary"
            size="small"
          >
            {value ? 'Change File' : 'Upload File'}
          </BaseButton>
        )}

        {value && (
          <>
            <Typography variant="caption" sx={{ color: '#4CAF50' }}>✓ Selected</Typography>
            <Chip
              label="Clear"
              size="small"
              icon={<CloseIcon />}
              onDelete={() => {
                onChange('');
                setMimeType('');
              }}
              onClick={() => {
                onChange('');
                setMimeType('');
              }}
              sx={{ fontSize: '11px', height: 20 }}
            />
          </>
        )}
      </Box>

      {/* Preview */}
      {isImage && (
        <Box sx={{ mt: 2 }}>
          <img
            src={value}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '8px',
              objectFit: 'contain',
              border: '1px solid #E6E7EB',
              padding: '8px',
            }}
          />
        </Box>
      )}
      {isVideo && (
        <Box sx={{ mt: 2 }}>
          <video
            src={value}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '8px',
              border: '1px solid #E6E7EB',
            }}
          />
        </Box>
      )}

      {/* Asset Picker Modal */}
      <BaseModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Choose from Asset Library"
        maxWidth="md"
        showFooter={false}
      >
        {loadingMedia ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : mediaList.length === 0 ? (
          <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            No media files found.
          </Typography>
        ) : (
          <Grid container spacing={1.5}>
            {mediaList.map((item) => {
              const itemId = item._id || item.id || '';
              const isImg = item.mimeType?.startsWith('image/');
              return (
                <Grid item xs={6} sm={4} md={3} key={itemId}>
                  <Card
                    elevation={0}
                    sx={{
                      border: value === item.url ? '2px solid #005e5d' : '1px solid #E6E7EB',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                  >
                    <CardActionArea onClick={() => handlePickMedia(item)}>
                      {isImg ? (
                        <CardMedia
                          component="img"
                          height="100"
                          image={item.url}
                          alt={item.originalName}
                          sx={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', fontSize: 32 }}>
                          📄
                        </Box>
                      )}
                      <Box sx={{ p: 0.75 }}>
                        <Typography variant="caption" noWrap display="block" title={item.originalName}>
                          {item.originalName}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </BaseModal>
    </Box>
  );
};
